import FormText from 'components/form/FormText';

import { useFieldArray, useFormContext } from 'react-hook-form';
import type { QuizEditFormStateType } from 'types/page/quizEditFormStateType';
import WsQuizEditQuestionOptionCorrectButton from 'pages/ws/quizEdit/components/quizEditQuestions/WsQuizEditQuestionOptionCorrectButton';
import { isEmpty } from 'utils/app';

import type { JSX } from 'react';

/**
 * クイズ作成編集 設問設定コンポーネント
 */
export default function WsQuizEditQuestionOptions(props: {
  questionIndex: number;
  readOnly: boolean;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  const {
    control,
    register,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext<QuizEditFormStateType>();

  const { append, move, remove, update, fields } = useFieldArray({
    control,
    name: `questions.${props.questionIndex}.options`,
  });

  // 登録可能最大件数
  const limit = 4;

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * オプションの順番を入れ替えます。
   */
  const onMove = (optionIndex: number, upper: boolean) => {
    // 先頭の場合は上に移動不可
    if (upper && optionIndex === 0) {
      return;
    }
    // 最終行の場合は下に移動不可
    if (!upper && optionIndex === fields.length - 1) {
      return;
    }
    // 要素の入れ替え
    move(optionIndex, upper ? optionIndex - 1 : optionIndex + 1);
  };

  /**
   * オプションを削除します。
   */
  const onRemove = (optionIndex: number) => {
    // ２件以下の場合は削除不可
    if (fields.length <= 2) {
      return;
    }
    // 要素の削除
    remove(optionIndex);

    // 正解の指定がなくなる場合は先頭に設定する
    const options = getValues().questions[props.questionIndex].options;
    if (isEmpty(options.filter(v => v.correctFlag))) {
      update(0, { ...options[0], correctFlag: true });
    }
  };

  /**
   * オプションを追加します。
   */
  const onAppend = () => {
    const values = getValues();
    const options = values.questions[props.questionIndex].options;

    // 上限以上の場合は追加不可
    if (options.length >= limit) {
      return;
    }
    // 要素の追加
    append({});
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <div className="form-group form-group-md">
        <div className="row">
          <div className="col">
            <label htmlFor="option1" className="form-label">
              選択肢
            </label>
          </div>
          <div className="col col-btn flex-grow-0">
            <label className="form-label">{'正　解'}</label>
          </div>
        </div>
        {/* {getValues().questions[props.questionIndex].options.map( */}
        {watch(`questions.${props.questionIndex}.options`).map((option, optionIndex) => {
          return (
            <div
              key={`ws-quiz-edit-question[${props.questionIndex}]-options-${optionIndex}`}
              className="form-group row align-items-center"
            >
              <div className="col-lg">
                <div className="form-group-row">
                  <div className="col col-number">
                    <label htmlFor="option1" className="number">
                      {optionIndex + 1}
                    </label>
                  </div>
                  <div className="col">
                    <FormText
                      name={`questions.${props.questionIndex}.options.${optionIndex}.title`}
                      placeholder="選択肢を入力しよう"
                      register={register}
                      errors={errors}
                      readOnly={props.readOnly}
                      maxLength={100}
                      className="form-control-md"
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg col-btn d-flex flex-grow-0 ms-auto text-nowrap">
                {/* 正解選択ボタン */}
                <WsQuizEditQuestionOptionCorrectButton
                  questionIndex={props.questionIndex}
                  optionIndex={optionIndex}
                  control={control}
                  readOnly={props.readOnly}
                />

                <div className="btn-group ms-auto">
                  <button
                    className="btn"
                    onClick={() => onMove(optionIndex, true)}
                    disabled={props.readOnly}
                  >
                    <img src="/img/icon/arrow-up-black.svg" alt="" />
                  </button>
                  <button
                    className="btn"
                    onClick={() => onMove(optionIndex, false)}
                    disabled={props.readOnly}
                  >
                    <img src="/img/icon/arrow-up-black.svg" alt="" className="scaley-1" />
                  </button>
                  <button
                    onClick={() => onRemove(optionIndex)}
                    disabled={props.readOnly}
                    className="btn"
                  >
                    <img src="/img/icon/trash.svg" alt="" className="filter-black" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        <div className="btn-container max-width-238 mx-auto">
          <button
            className="btn btn-common moderate-blue w-100"
            onClick={onAppend}
            disabled={fields.length >= limit || props.readOnly}
          >
            + 選択肢を追加
          </button>
        </div>
      </div>
    </>
  );
}
